import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Typography, Divider } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, GoogleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { auth, signInWithGoogle, db } from '../firebase-config';
import { 
  createUserWithEmailAndPassword, 
  updateProfile, 
  onAuthStateChanged,
  fetchSignInMethodsForEmail 
} from 'firebase/auth';
import { setDoc, doc } from 'firebase/firestore';

const { Title } = Typography;

const SignUp = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if user is already signed in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/edit');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Check if email is already in use
      const signInMethods = await fetchSignInMethodsForEmail(auth, values.email);
      
      if (signInMethods.length > 0) {
        // Email exists, check the sign-in method
        if (signInMethods.includes('google.com')) {
          message.error('This email is already registered with Google. Please sign in with Google.');
        } else {
          message.error('This email is already registered. Please sign in with email and password.');
        }
        setLoading(false);
        return;
      }

      // If email is not in use, proceed with registration
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );
      
      await updateProfile(userCredential.user, {
        displayName: values.username
      });

      // Save user data to Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: values.email,
        username: values.username,
        subscription: false,
        trial: 6,
        createdAt: new Date().toISOString(),
      });

      message.success('Welcome to the family! 🎉');
      navigate('/edit');
    } catch (error) {
      console.error('Sign up error:', error);
      
      // Handle specific error cases
      switch (error.code) {
        case 'auth/email-already-in-use':
          message.error('This email is already registered. Please sign in.');
          break;
        case 'auth/invalid-email':
          message.error('Please enter a valid email address.');
          break;
        case 'auth/operation-not-allowed':
          message.error('Email/password accounts are not enabled. Please contact support.');
          break;
        case 'auth/weak-password':
          message.error('Please choose a stronger password.');
          break;
        default:
          message.error('Sign up failed 😕 Please try again');
      }
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    try {
      // Get the Google sign-in result first without completing the sign-in
      const result = await signInWithGoogle();
      const email = result.user.email;

      // Check existing sign-in methods for this email
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      if (signInMethods.includes('password')) {
        // Email exists with password authentication
        message.error('This email is already registered with email/password. Please sign in with your password.');
        // Sign out the user since we don't want to complete the Google sign-in
        await auth.signOut();
        return;
      }

      // If we get here, either the email is new or already registered with Google
      message.success('Welcome aboard! 🎉');
      navigate('/edit');

    } catch (error) {
      console.error('Google sign-up error:', error);
      if (error.code === 'auth/account-exists-with-different-credential') {
        message.error('An account already exists with this email. Please use email/password to sign in.');
      } else {
        message.error('Google sign-up failed 😕');
      }
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Branding Section */}
      <div className="absolute top-0 left-0 right-0 text-center py-8">
        <h1 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-tr from-pink-600/30 to-indigo-800/30">
          CarouselPost.in
        </h1>
        <p className="mt-2 text-lg text-gray-600 font-medium">
          Create Stunning LinkedIn Carousels in Minutes ✨
        </p>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/90 rounded-2xl shadow-xl p-8 space-y-6 border border-white/50">
          <div className="text-center space-y-2">
            <Title level={5} className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Join the Adventure! ✨
            </Title>
            <p className="text-gray-500">Create your account and start creating</p>
          </div>

          <Form
            name="signup"
            onFinish={onFinish}
            layout="vertical"
            className="space-y-4"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Please input your username!' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Username"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Email"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' },
                { min: 6, message: 'Password must be at least 6 characters!' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item
              name="confirm"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm Password"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg"
              >
                Sign Up 🚀
              </Button>
            </Form.Item>
          </Form>

          <Divider plain>or</Divider>

          <Button
            onClick={handleGoogleSignIn}
            block
            size="large"
            icon={<GoogleOutlined />}
            className="rounded-lg border-2 hover:border-purple-400 hover:text-purple-600"
          >
            Continue with Google
          </Button>

          <div className="text-center mt-4">
            <span className="text-gray-500">Already have an account? </span>
            <Link to="/signin" className="text-purple-600 hover:text-purple-800 font-medium">
              Sign In
            </Link>
          </div>
        </div>
      </div>

      {/* Add subtle pattern overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
    </div>
  );
};

export default SignUp;
